// frontend/src/utils/taxCalculator.js
/**
 * Система налогообложения для Mailsball (Эстония)
 * Клиентская версия налогового калькулятора
 */

// Эстонская ставка НДС 24%
const ESTONIAN_VAT_RATE = 0.24;

// Список стран ЕС для проверки
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK'
];

// Коды стран для конвертации
const COUNTRY_CODES = {
  'Austria': 'AT', 'Belgium': 'BE', 'Bulgaria': 'BG', 'Cyprus': 'CY', 'Czech Republic': 'CZ',
  'Germany': 'DE', 'Denmark': 'DK', 'Estonia': 'EE', 'Spain': 'ES', 'Finland': 'FI',
  'France': 'FR', 'Greece': 'GR', 'Croatia': 'HR', 'Hungary': 'HU', 'Ireland': 'IE',
  'Italy': 'IT', 'Latvia': 'LV', 'Lithuania': 'LT', 'Luxembourg': 'LU', 'Malta': 'MT',
  'Netherlands': 'NL', 'Poland': 'PL', 'Portugal': 'PT', 'Romania': 'RO', 'Sweden': 'SE',
  'Slovenia': 'SI', 'Slovakia': 'SK'
};

/**
 * Получает код страны из названия
 * @param {string} countryName - Название страны
 * @returns {string} Код страны
 */
function getCountryCode(countryName) {
  if (!countryName) return '';
  return COUNTRY_CODES[countryName] || countryName.toUpperCase();
}

/**
 * Определяет, является ли страна страной ЕС
 * @param {string} countryName - Название страны
 * @returns {boolean}
 */
function isEUCountry(countryName) {
  try {
    const countryCode = getCountryCode(countryName);
    return EU_COUNTRIES.includes(countryCode);
  } catch (error) {
    console.error('Error checking EU country:', error);
    return false;
  }
}

/**
 * Определяет, является ли страна Эстонией
 * @param {string} countryName - Название страны
 * @returns {boolean}
 */
function isEstonia(countryName) {
  try {
    const countryCode = getCountryCode(countryName);
    return countryCode === 'EE';
  } catch (error) {
    console.error('Error checking Estonia:', error);
    return false;
  }
}

/**
 * Определяет тип клиента и рассчитывает налоги
 * @param {Object} customerData - Данные о клиенте
 * @param {string} customerData.billingCountry - Страна биллинга
 * @param {boolean} customerData.billingHasVat - Есть ли VAT номер
 * @param {string} customerData.billingVatNumber - VAT номер
 * @param {boolean} customerData.isCompany - Является ли компанией
 * @param {number} baseAmount - Базовая сумма без налогов
 * @returns {Object} Результат расчета налогов
 */
function calculateTax(customerData, baseAmount) {
  const {
    billingCountry,
    billingHasVat = false,
    billingVatNumber = '',
    isCompany = false
  } = customerData;

  // Проверяем валидность данных
  if (!billingCountry || !baseAmount || baseAmount <= 0) {
    return {
      baseAmount: baseAmount || 0,
      taxAmount: 0,
      taxRate: 0,
      totalAmount: baseAmount || 0,
      taxDescription: 'No tax - invalid data',
      customerType: 'Unknown',
      isEUCountry: false,
      isEstonia: false,
      shouldApplyTax: false
    };
  }

  const isEU = isEUCountry(billingCountry);
  const isEE = isEstonia(billingCountry);
  const hasValidVAT = billingHasVat && billingVatNumber && billingVatNumber.length > 0;

  let taxRate = 0;
  let shouldApplyTax = false;
  let taxDescription = '';
  let customerType = '';

  // Применяем правила налогообложения согласно требованиям:
  // B2C - в нашей стране - Налог начисляется - ставка 24%
  // B2C - в ЕС - Налог начисляется - ставка 24%
  // B2C - Вне ЕС - Налога нет - ставка 0%
  // B2B - В ЕС с VAT номером - Налог начисляется - ставка 24%
  // B2B - В ЕС без VAT номера - Налог начисляется - ставка 24%
  // B2B - Не в ЕС - Налога нет - ставка 0%
  
  if (isCompany) {
    // B2B клиент
    if (isEU) {
      if (hasValidVAT) {
        // B2B - В ЕС с VAT номером - Налог начисляется - ставка 24%
        taxRate = ESTONIAN_VAT_RATE;
        shouldApplyTax = true;
        taxDescription = 'Estonian VAT';
        customerType = 'B2B EU with VAT';
      } else {
        // B2B - В ЕС без VAT номера - Налог начисляется - ставка 24%
        taxRate = ESTONIAN_VAT_RATE;
        shouldApplyTax = true;
        taxDescription = 'Estonian VAT';
        customerType = 'B2B EU without VAT';
      }
    } else {
      // B2B - Не в ЕС - Налога нет - ставка 0%
      taxRate = 0;
      shouldApplyTax = false;
      taxDescription = 'No tax (non-EU)';
      customerType = 'B2B non-EU';
    }
  } else {
    // B2C клиент
    if (isEE) {
      // B2C - в нашей стране (Эстония) - Налог начисляется - ставка 24%
      taxRate = ESTONIAN_VAT_RATE;
      shouldApplyTax = true;
      taxDescription = 'Estonian VAT';
      customerType = 'B2C Estonia';
    } else if (isEU) {
      // B2C - в ЕС - Налог начисляется - ставка 24%
      taxRate = ESTONIAN_VAT_RATE;
      shouldApplyTax = true;
      taxDescription = 'Estonian VAT';
      customerType = 'B2C EU';
    } else {
      // B2C - Вне ЕС - Налога нет - ставка 0%
      taxRate = 0;
      shouldApplyTax = false;
      taxDescription = 'No tax (non-EU)';
      customerType = 'B2C non-EU';
    }
  }

  const taxAmount = shouldApplyTax ? baseAmount * taxRate : 0;
  const totalAmount = baseAmount + taxAmount;

  return {
    baseAmount: Number(baseAmount.toFixed(2)),
    taxAmount: Number(taxAmount.toFixed(2)),
    taxRate: Number((taxRate * 100).toFixed(2)), // Возвращаем в процентах
    totalAmount: Number(totalAmount.toFixed(2)),
    taxDescription,
    customerType,
    isEUCountry: isEU,
    isEstonia: isEE,
    shouldApplyTax
  };
}

/**
 * Форматирует отображение цены с налогами
 * @param {Object} taxCalculation - Результат расчета налогов
 * @returns {Object} Форматированная информация о цене
 */
function formatPriceWithTax(taxCalculation) {
  const { baseAmount, taxAmount, taxRate, totalAmount, shouldApplyTax } = taxCalculation;
  
  return {
    baseAmountFormatted: `€${baseAmount.toFixed(2)}`,
    taxAmountFormatted: shouldApplyTax ? `€${taxAmount.toFixed(2)} (${taxRate}%)` : 'No tax',
    totalAmountFormatted: `€${totalAmount.toFixed(2)}`,
    taxInfoText: shouldApplyTax ? `includes €${taxAmount.toFixed(2)} VAT` : 'tax-free',
    breakdown: {
      base: `€${baseAmount.toFixed(2)}`,
      tax: shouldApplyTax ? `€${taxAmount.toFixed(2)}` : '€0.00',
      total: `€${totalAmount.toFixed(2)}`
    }
  };
}

export {
  calculateTax,
  formatPriceWithTax,
  isEUCountry,
  isEstonia,
  ESTONIAN_VAT_RATE,
  EU_COUNTRIES
}; 